<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useConvexQuery, useConvexClient } from 'convex-vue';
import { useConvexQueryWithStaleDetection } from '../composables/useConvexQueryWithStaleDetection';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type { IdeaWithVotes } from '../types';
import { useMobile } from '../composables/useMobile';
import { useUserSettings } from '../composables/useUserSettings';
import { throttle } from '../composables/useThrottledResize';
import PostitCard from '../components/PostitCard.vue';
import IdeaListView from '../components/IdeaListView.vue';
import FilterButton from '../components/FilterButton.vue';
import FilterDrawer from '../components/FilterDrawer.vue';
import AddIdeaModal from '../components/AddIdeaModal.vue';
import IdeaDetailPanel from '../components/IdeaDetailPanel.vue';
import Header from '../components/Header.vue';
import ConfirmDialog from '../components/ConfirmDialog.vue';
import Toast from '../components/Toast.vue';
import SettingsPanel from '../components/SettingsPanel.vue';
import AccessManager from '../components/AccessManager.vue';
import SupportersModal from '../components/SupportersModal.vue';
import CommentsModal from '../components/CommentsModal.vue';
import VoteLockedBanner from '../components/VoteLockedBanner.vue';
import WelcomeModal from '../components/WelcomeModal.vue';
import Plus from '../components/icons/Plus.vue';
import Download from '../components/icons/Download.vue';
import Upload from '../components/icons/Upload.vue';

import X from '../components/icons/X.vue';
import Settings from '../components/icons/Settings.vue';
import Users from '../components/icons/Users.vue';
import HandHelping from '../components/icons/HandHelping.vue';
import MessageSquare from '../components/icons/MessageSquare.vue';

const { t } = useI18n();

const route = useRoute();
const client = useConvexClient();
const { isMobile } = useMobile();

const slug = computed(() => route.params.slug as string);

// Window width for responsive checks
const windowWidth = ref(window.innerWidth);
const isDesktop = computed(() => windowWidth.value >= 1024);

const updateWindowWidth = throttle(() => {
  windowWidth.value = window.innerWidth;
}, 150);

const selectedIdeaId = ref<string | null>(null);
const isDetailPanelOpen = ref(false);

// Make selectedIdea a computed property so it always references the latest data
const selectedIdea = computed<IdeaWithVotes | null>(() => {
  if (!selectedIdeaId.value) return null;
  return rawIdeas.value.find(idea => idea.id === selectedIdeaId.value) || null;
});
const fileInput = ref<HTMLInputElement | null>(null);
const editingIdea = ref<IdeaWithVotes | null>(null);
const isEditModalOpen = ref(false);
const isFilterOpen = ref(false);
const filterHeight = ref(0);

// Settings and Invite modals
const showSettings = ref(false);
const showInviteManager = ref(false);
const showSupporters = ref(false);
const showComments = ref(false);
const isAddModalOpen = ref(false);
const menuOpen = ref(false);
const showWelcomeModal = ref(false);
const selectedCommentIdeaId = ref<string | null>(null);

// Filter state
const selectedTags = ref<string[]>([]);
const sortBy = ref<'liked' | 'important' | 'supported' | 'total' | 'newest'>('newest');
const minScore = ref(0);
const searchQuery = ref('');
const groupedDots = ref(false);
const groupByTag = ref(false);
const displayMode = ref<'grid' | 'list'>('grid');
const visibleVoteTypes = ref<('liked' | 'important' | 'supported')[]>([
  'liked',
  'important',
  'supported',
]);

// Get workshop data with stale connection detection
const {
  data: workshop,
  isPending: workshopLoading,
  isReconnecting,
} = useConvexQueryWithStaleDetection(api.workshops.getBySlug, () => ({ slug: slug.value }), {
  name: 'WorkshopBoard',
});

// Get ideas - only query when workshop is loaded
const workshopId = computed(() => workshop.value?.id);

// Use reactive Convex query for ideas - auto-updates when data changes
const ideasQuery = useConvexQuery(api.ideas.list, () => {
  const id = workshopId.value;
  if (!id) return { workshopId: undefined };
  return { workshopId: id };
});

const rawIdeas = computed(() => (ideasQuery.data.value || []) as IdeaWithVotes[]);
const isLoading = computed(() => workshopLoading.value || ideasQuery.isPending.value);
const canWrite = computed(() => ['owner', 'member'].includes(workshop.value?.role || ''));
const isOwner = computed(() => workshop.value?.role === 'owner');
const votesLocked = computed(() => workshop.value?.votesLocked ?? false);
const commentsEnabled = computed(() => workshop.value?.commentsEnabled ?? true);

// Get current user and settings for prefill feature
const { data: currentUser } = useConvexQuery(api.auth.currentUser, () => ({}));
const { data: userSettings, isPending: settingsPending } = useUserSettings();

// Get workshop preferences for current user
const { data: workshopPreferences } = useConvexQuery(api.workshopPreferences.get, () => {
  const id = workshopId.value;
  if (!id) return { workshopId: undefined };
  return { workshopId: id };
});

const userName = computed(() => currentUser.value?.name || '');
const prefillNameEnabled = computed(() => userSettings.value?.prefillNameOnIdeas ?? true);

// Load preferences when available
watch(
  workshopPreferences,
  preferences => {
    if (preferences && workshopId.value) {
      sortBy.value = preferences.sortBy;
      selectedTags.value = [...preferences.selectedTags];
      minScore.value = preferences.minScore;
      searchQuery.value = preferences.searchQuery;
      groupedDots.value = preferences.groupedDots;
      groupByTag.value = preferences.groupByTag;
      displayMode.value = preferences.displayMode ?? 'grid';
      visibleVoteTypes.value = [...preferences.visibleVoteTypes];
    }
  },
  { immediate: true }
);

// Show welcome modal when settings are loaded and user hasn't dismissed it
watch(
  () => settingsPending.value,
  pending => {
    if (!pending && userSettings.value && !userSettings.value.hideWelcomeModal) {
      showWelcomeModal.value = true;
    }
  },
  { immediate: true }
);

const handleHideWelcomeModal = async () => {
  try {
    await client.mutation(api.userSettings.update, { hideWelcomeModal: true });
  } catch (e) {
    console.error('Failed to hide welcome modal:', e);
  }
};

const handleReopenWelcomeModal = async () => {
  try {
    await client.mutation(api.userSettings.update, { hideWelcomeModal: false });
    showWelcomeModal.value = true;
  } catch (e) {
    console.error('Failed to reopen welcome modal:', e);
  }
};

// Save preferences when they change (debounced)
const savePreferences = debounce(async () => {
  if (!workshopId.value || !currentUser.value) return;
  try {
    await client.mutation(api.workshopPreferences.update, {
      workshopId: workshopId.value,
      sortBy: sortBy.value,
      selectedTags: selectedTags.value,
      minScore: minScore.value,
      searchQuery: searchQuery.value,
      groupedDots: groupedDots.value,
      groupByTag: groupByTag.value,
      displayMode: displayMode.value,
      visibleVoteTypes: visibleVoteTypes.value,
    });
  } catch (e) {
    console.error('Failed to save preferences:', e);
  }
}, 1000);

// Watch for changes and save them
// Note: removed deep: true since primitives are replaced, not mutated
watch(
  [
    sortBy,
    selectedTags,
    minScore,
    searchQuery,
    groupedDots,
    groupByTag,
    displayMode,
    visibleVoteTypes,
  ],
  () => {
    savePreferences();
  }
);

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  }) as T;
}

// Calculate available height for main content
const mainContentHeight = computed(() => {
  if (isMobile.value) return 'auto';
  const headerHeight = 70;
  if (!isFilterOpen.value) {
    return `calc(100vh - ${headerHeight}px)`;
  }
  const filterOffset = Math.max(filterHeight.value, 200);
  return `calc(100vh - ${headerHeight + filterOffset}px)`;
});

// Update filter height when it changes
function updateFilterHeight() {
  const filterEl = document.querySelector('.filter-drawer-desktop');
  if (filterEl) {
    filterHeight.value = filterEl.getBoundingClientRect().height;
  }
}

// Watch for filter open state changes
watch(isFilterOpen, isOpen => {
  if (isOpen && !isMobile.value) {
    setTimeout(updateFilterHeight, 350);
  }
});

// Reset comment state when modal closes
watch(showComments, isOpen => {
  if (!isOpen) {
    selectedCommentIdeaId.value = null;
  }
});

// Confirm dialog state
const showResetConfirm = ref(false);
const showImportConfirm = ref(false);
const pendingImportFile = ref<File | null>(null);

// Toast ref
const toastRef = ref<InstanceType<typeof Toast> | null>(null);

// All unique tags from ideas
const allTags = computed(() => {
  if (!rawIdeas.value) return [];
  const tagSet = new Set<string>();
  rawIdeas.value.forEach(idea => {
    idea.tags?.forEach(tag => tagSet.add(tag));
  });
  return Array.from(tagSet).sort();
});

// Filtered ideas
const filteredIdeas = computed(() => {
  if (!rawIdeas.value) return [];

  let ideas = [...rawIdeas.value];

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    ideas = ideas.filter(
      idea =>
        idea.title.toLowerCase().includes(query) ||
        idea.explanation?.toLowerCase().includes(query) ||
        idea.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  }

  // Tag filter
  if (selectedTags.value.length > 0) {
    ideas = ideas.filter(idea => idea.tags?.some(tag => selectedTags.value.includes(tag)));
  }

  // Helper to calculate visible total
  const getVisibleTotal = (idea: IdeaWithVotes) => {
    return visibleVoteTypes.value.reduce((sum, type) => sum + (idea[type] || 0), 0);
  };

  // Min score filter (uses visible total)
  if (minScore.value > 0) {
    ideas = ideas.filter(idea => {
      return getVisibleTotal(idea) >= minScore.value;
    });
  }

  // Sort
  switch (sortBy.value) {
    case 'newest':
      ideas.sort((a, b) => b.createdAt - a.createdAt);
      break;
    case 'liked':
      ideas.sort((a, b) => b.liked - a.liked);
      break;
    case 'important':
      ideas.sort((a, b) => b.important - a.important);
      break;
    case 'supported':
      ideas.sort((a, b) => b.supported - a.supported);
      break;
    case 'total':
    default:
      ideas.sort((a, b) => {
        return getVisibleTotal(b) - getVisibleTotal(a);
      });
      break;
  }

  return ideas;
});

// Grouped by tag
const groupedByTag = computed(() => {
  if (!groupByTag.value) return null;

  const groups: Record<string, IdeaWithVotes[]> = {};
  const untagged: IdeaWithVotes[] = [];

  filteredIdeas.value.forEach(idea => {
    if (idea.tags?.length > 0) {
      idea.tags.forEach(tag => {
        if (!groups[tag]) groups[tag] = [];
        groups[tag].push(idea);
      });
    } else {
      untagged.push(idea);
    }
  });

  // Helper to calculate visible total
  const getVisibleTotal = (idea: IdeaWithVotes) => {
    return visibleVoteTypes.value.reduce((sum, type) => sum + (idea[type] || 0), 0);
  };

  // Sort ideas within each group based on sortBy
  const sortIdeas = (ideas: IdeaWithVotes[]) => {
    const sorted = [...ideas];
    switch (sortBy.value) {
      case 'newest':
        sorted.sort((a, b) => b.createdAt - a.createdAt);
        break;
      case 'liked':
        sorted.sort((a, b) => b.liked - a.liked);
        break;
      case 'important':
        sorted.sort((a, b) => b.important - a.important);
        break;
      case 'supported':
        sorted.sort((a, b) => b.supported - a.supported);
        break;
      case 'total':
      default:
        sorted.sort((a, b) => {
          return getVisibleTotal(b) - getVisibleTotal(a);
        });
        break;
    }
    return sorted;
  };

  const sortedGroups: Record<string, IdeaWithVotes[]> = {};
  Object.keys(groups).forEach(tag => {
    sortedGroups[tag] = sortIdeas(groups[tag]);
  });

  const sortedTags = Object.keys(sortedGroups).sort();

  return { groups: sortedGroups, sortedTags, untagged: sortIdeas(untagged) };
});

const activeFilterCount = computed(() => {
  let count = 0;
  if (selectedTags.value.length > 0) count += selectedTags.value.length;
  if (minScore.value > 0) count += 1;
  if (searchQuery.value) count += 1;
  return count;
});

const hasActiveFilters = computed(() => activeFilterCount.value > 0);

const filteredCount = computed(() => filteredIdeas.value.length);
const totalIdeas = computed(() => rawIdeas.value?.length || 0);

onMounted(() => {
  window.addEventListener('resize', updateWindowWidth);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateWindowWidth);
});

// Convex mutations
const handleVote = async (
  ideaId: string,
  voteType: 'liked' | 'important' | 'supported',
  onError?: () => void
) => {
  try {
    await client.mutation(api.votes.toggle, {
      ideaId: ideaId as Id<'ideas'>,
      voteType,
    });
  } catch (e: any) {
    console.error('Vote failed:', e);
    toastRef.value?.error(t('toast.failedToVote'));
    // Call the error callback to revert optimistic update
    onError?.();
  }
};

const handleDelete = async (ideaId: string) => {
  try {
    await client.mutation(api.ideas.remove, { ideaId: ideaId as Id<'ideas'> });
    toastRef.value?.success(t('toast.ideaDeleted'));
  } catch (e: any) {
    toastRef.value?.error(t('toast.failedToDelete'));
    console.error(e);
  }
};

const handleAddIdea = async (data: any) => {
  if (!workshop.value?.id || !canWrite.value) return;
  try {
    await client.mutation(api.ideas.create, {
      workshopId: workshop.value.id as Id<'workshops'>,
      ...data,
    });
    isAddModalOpen.value = false;
    toastRef.value?.success(t('toast.ideaAdded'));
  } catch (e: any) {
    toastRef.value?.error(t('toast.failedToAddIdea'));
    console.error(e);
  }
};

const handleSaveEdit = async (updatedIdea: Partial<IdeaWithVotes>) => {
  if (!editingIdea.value) return;
  try {
    await client.mutation(api.ideas.update, {
      ideaId: editingIdea.value.id as Id<'ideas'>,
      title: updatedIdea.title,
      person: updatedIdea.person,
      explanation: updatedIdea.explanation,
      tags: updatedIdea.tags,
      color: updatedIdea.color,
      rotation: updatedIdea.rotation,
    });
    isEditModalOpen.value = false;
    editingIdea.value = null;
    toastRef.value?.success(t('toast.ideaUpdated'));
  } catch (e: any) {
    toastRef.value?.error(t('toast.failedToUpdateIdea'));
    console.error(e);
  }
};

function handleCardClick(idea: IdeaWithVotes) {
  selectedIdeaId.value = idea.id;
  isDetailPanelOpen.value = true;
}

function handleEdit(ideaId: string) {
  const idea = rawIdeas.value?.find(i => i.id === ideaId);
  if (idea) {
    editingIdea.value = idea;
    isEditModalOpen.value = true;
  }
}

function handleDetailEdit() {
  if (selectedIdea.value) {
    editingIdea.value = selectedIdea.value;
    isDetailPanelOpen.value = false;
    isEditModalOpen.value = true;
  }
}

function handleCommentClick(ideaId: string) {
  if (!commentsEnabled.value) return;
  selectedCommentIdeaId.value = ideaId;
  showComments.value = true;
}

function handleDetailComment() {
  if (!commentsEnabled.value) return;
  if (selectedIdea.value) {
    selectedCommentIdeaId.value = selectedIdea.value.id;
    isDetailPanelOpen.value = false;
    showComments.value = true;
  }
}

function handleOpenWorkshopComments() {
  selectedCommentIdeaId.value = null;
  showComments.value = true;
}

function toggleTag(tag: string) {
  const index = selectedTags.value.indexOf(tag);
  if (index === -1) {
    selectedTags.value.push(tag);
  } else {
    selectedTags.value.splice(index, 1);
  }
}

function toggleVoteType(type: 'liked' | 'important' | 'supported') {
  const index = visibleVoteTypes.value.indexOf(type);
  if (index === -1) {
    visibleVoteTypes.value.push(type);
  } else {
    visibleVoteTypes.value.splice(index, 1);
  }
}

async function handleExport() {
  // Fetch all comments for this workshop
  let exportData: any = { ideas: rawIdeas.value, comments: {} };

  if (workshop.value?.id) {
    try {
      // Get comments for each idea
      const commentsByIdea: Record<string, any[]> = {};
      for (const idea of rawIdeas.value) {
        const ideaComments = await client.query(api.comments.listByIdea, {
          ideaId: idea.id as Id<'ideas'>,
        });
        if (ideaComments && ideaComments.length > 0) {
          commentsByIdea[idea.id] = ideaComments;
        }
      }
      exportData.comments = commentsByIdea;
    } catch (e) {
      console.error('Failed to fetch comments for export:', e);
    }
  }

  const data = JSON.stringify(exportData, null, 2);
  const blob = new Blob([data], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `workshop-${slug.value}-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  menuOpen.value = false;
  toastRef.value?.success('Workshop exported!');
}

function handleImportClick() {
  fileInput.value?.click();
  menuOpen.value = false;
}

function handleFileImport(event: Event) {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];
  if (!file) return;
  pendingImportFile.value = file;
  showImportConfirm.value = true;
  target.value = '';
}

async function confirmImport() {
  if (!pendingImportFile.value || !workshop.value?.id) return;

  // Only owners can import
  if (!isOwner.value) {
    toastRef.value?.error(t('toast.importOwnerOnly'));
    pendingImportFile.value = null;
    showImportConfirm.value = false;
    return;
  }

  const reader = new FileReader();
  reader.onload = async e => {
    try {
      const data = JSON.parse(e.target?.result as string);
      // Handle both old format (array) and new format ({ ideas: [...], comments: {} })
      const ideasArray = Array.isArray(data) ? data : data.ideas;
      if (Array.isArray(ideasArray)) {
        // Use the protected importToWorkshop mutation
        await client.mutation(api.importExport.importToWorkshop, {
          workshopId: workshop.value!.id as Id<'workshops'>,
          ideas: ideasArray.map((idea: any) => ({
            title: idea.title,
            person: idea.person,
            explanation: idea.explanation,
            tags: idea.tags ? idea.tags.map((tag: string) => tag.trim().toLowerCase()) : [],
            color: idea.color,
            rotation: idea.rotation,
            liked: idea.liked || 0,
            important: idea.important || 0,
            supported: idea.supported || 0,
          })),
        });
        toastRef.value?.success(t('toast.ideasImported'));
      } else {
        toastRef.value?.error(t('toast.invalidFileFormat'));
      }
    } catch (err: any) {
      toastRef.value?.error(err.message || t('toast.failedToImport'));
      console.error(err);
    }
    pendingImportFile.value = null;
  };
  reader.readAsText(pendingImportFile.value);
  showImportConfirm.value = false;
}

function handleReset() {
  showResetConfirm.value = true;
  menuOpen.value = false;
}

function confirmReset() {
  showResetConfirm.value = false;
  // Reset filters
  selectedTags.value = [];
  minScore.value = 0;
  searchQuery.value = '';
  toastRef.value?.success('Filters reset');
}

function clearFilters() {
  selectedTags.value = [];
  minScore.value = 0;
  searchQuery.value = '';
}
</script>

<template>
  <div class="cork-board min-h-screen" :class="{ 'h-screen overflow-hidden': !isMobile }">
    <!-- Header -->
    <Header
      :filtered-count="filteredCount"
      :total-ideas="totalIdeas"
      :menu-open="menuOpen"
      :workshop-name="workshop?.name || slug"
      :is-owner="isOwner"
      :can-write="canWrite"
      @toggle-menu="menuOpen = !menuOpen"
      @add-idea="isAddModalOpen = true"
      @export="handleExport"
      @import="handleImportClick"
      @reset="handleReset"
      @open-settings="showSettings = true"
      @open-invites="showInviteManager = true"
      @open-supporters="showSupporters = true"
      @open-comments="handleOpenWorkshopComments"
    />

    <!-- Vote Locked Banner -->
    <VoteLockedBanner v-if="votesLocked" />

    <!-- Filter Button -->
    <FilterButton
      :active-filter-count="activeFilterCount"
      :is-open="isFilterOpen"
      @click="isFilterOpen = !isFilterOpen"
    />

    <!-- Filter Drawer -->
    <FilterDrawer
      :is-open="isFilterOpen"
      :is-mobile="isMobile"
      :all-tags="allTags"
      :selected-tags="selectedTags"
      :sort-by="sortBy"
      :min-score="minScore"
      :search-query="searchQuery"
      :grouped-dots="groupedDots"
      :visible-vote-types="visibleVoteTypes"
      :group-by-tag="groupByTag"
      :display-mode="displayMode"
      @close="isFilterOpen = false"
      @update:selected-tags="selectedTags = $event"
      @update:sort-by="sortBy = $event"
      @update:min-score="minScore = $event"
      @update:search-query="searchQuery = $event"
      @update:grouped-dots="groupedDots = $event"
      @update:group-by-tag="groupByTag = $event"
      @update:display-mode="displayMode = $event"
      @toggle-vote-type="toggleVoteType($event)"
      @clear-filters="clearFilters"
    />

    <!-- Loading state -->
    <div v-if="isLoading" class="flex items-center justify-center h-64">
      <p class="text-white/70 text-lg">{{ $t('workshop.loading') }}</p>
    </div>

    <!-- Workshop not found or reconnecting -->
    <div v-else-if="!workshop" class="flex items-center justify-center h-64">
      <div v-if="isReconnecting" class="text-center">
        <div
          class="inline-block w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin mb-3"
        ></div>
        <p class="text-white/70 text-lg">{{ $t('workshop.reconnecting') }}</p>
      </div>
      <p v-else class="text-white/70 text-lg">{{ $t('workshop.notFound') }}</p>
    </div>

    <!-- Main content -->
    <main
      v-else
      class="px-4 py-6 pb-20 md:pb-0 overflow-y-auto scrollbar-white"
      :style="!isMobile ? { height: mainContentHeight } : {}"
    >
      <div class="max-w-7xl mx-auto pb-16">
        <!-- Tag-based section layout -->
        <div v-if="groupByTag && groupedByTag" class="space-y-8">
          <section v-for="tag in groupedByTag.sortedTags" :key="tag" class="tag-section">
            <h2
              class="text-xl font-bold text-white mb-4 sticky top-0 z-10 bg-cork-dark py-2 px-4 rounded-lg inline-block capitalize"
            >
              {{ tag }}
              <span class="text-sm font-normal text-white/70 ml-2">
                ({{ groupedByTag.groups[tag].length }})
              </span>
            </h2>
            <IdeaListView
              v-if="displayMode === 'list'"
              :ideas="groupedByTag.groups[tag]"
              :is-mobile="isMobile"
              :visible-vote-types="visibleVoteTypes"
              :current-user-id="currentUser?.userId"
              :user-role="workshop?.role"
              :sort-by="sortBy"
              :votes-locked="votesLocked"
              :comments-enabled="commentsEnabled"
              @vote="handleVote"
              @edit="handleEdit"
              @delete="handleDelete"
              @tag-click="toggleTag"
              @update:sort-by="sortBy = $event"
              @comment="handleCommentClick"
            />
            <div
              v-else
              :class="[
                'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 relative z-0',
                !isMobile ? 'pr-16' : '',
              ]"
            >
              <PostitCard
                v-for="idea in groupedByTag.groups[tag]"
                :key="`${tag}-${idea.id}`"
                :idea="idea"
                :grouped-dots="groupedDots"
                :is-mobile="isMobile"
                :visible-vote-types="visibleVoteTypes"
                :current-user-id="currentUser?.userId"
                :user-role="workshop?.role"
                :votes-locked="votesLocked"
                :comments-enabled="commentsEnabled"
                :is-detail-open="isDetailPanelOpen && selectedIdeaId === idea.id"
                @click="handleCardClick(idea)"
                @delete="handleDelete"
                @vote="handleVote"
                @edit="handleEdit"
                @tag-click="toggleTag"
                @comment="handleCommentClick"
              />
            </div>
          </section>

          <!-- Untagged section -->
          <section v-if="groupedByTag.untagged.length > 0" class="tag-section">
            <h2
              class="text-xl font-bold text-white/80 mb-4 sticky top-0 z-10 bg-cork-dark py-2 px-4 rounded-lg inline-block"
            >
              Untagged
              <span class="text-sm font-normal text-white/50 ml-2">
                ({{ groupedByTag.untagged.length }})
              </span>
            </h2>
            <IdeaListView
              v-if="displayMode === 'list'"
              :ideas="groupedByTag.untagged"
              :is-mobile="isMobile"
              :visible-vote-types="visibleVoteTypes"
              :current-user-id="currentUser?.userId"
              :user-role="workshop?.role"
              :sort-by="sortBy"
              :votes-locked="votesLocked"
              @vote="handleVote"
              @edit="handleEdit"
              @delete="handleDelete"
              @tag-click="toggleTag"
              @update:sort-by="sortBy = $event"
            />
            <div
              v-else
              :class="[
                'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 relative z-0',
                !isMobile ? 'pr-16' : '',
              ]"
            >
              <PostitCard
                v-for="idea in groupedByTag.untagged"
                :key="`untagged-${idea.id}`"
                :idea="idea"
                :grouped-dots="groupedDots"
                :is-mobile="isMobile"
                :visible-vote-types="visibleVoteTypes"
                :current-user-id="currentUser?.userId"
                :user-role="workshop?.role"
                :votes-locked="votesLocked"
                :is-detail-open="isDetailPanelOpen && selectedIdeaId === idea.id"
                @click="handleCardClick(idea)"
                @delete="handleDelete"
                @vote="handleVote"
                @edit="handleEdit"
                @tag-click="toggleTag"
              />
            </div>
          </section>
        </div>

        <!-- Regular grid layout -->
        <IdeaListView
          v-if="displayMode === 'list'"
          :ideas="filteredIdeas"
          :is-mobile="isMobile"
          :visible-vote-types="visibleVoteTypes"
          :current-user-id="currentUser?.userId"
          :user-role="workshop?.role"
          :sort-by="sortBy"
          :votes-locked="votesLocked"
          :comments-enabled="commentsEnabled"
          @vote="handleVote"
          @edit="handleEdit"
          @delete="handleDelete"
          @tag-click="toggleTag"
          @update:sort-by="sortBy = $event"
          @comment="handleCommentClick"
        />
        <div
          v-else
          :class="[
            'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 relative z-0',
            !isMobile ? 'pr-16' : '',
          ]"
        >
          <PostitCard
            v-for="idea in filteredIdeas"
            :key="idea.id"
            :idea="idea"
            :grouped-dots="groupedDots"
            :is-mobile="isMobile"
            :visible-vote-types="visibleVoteTypes"
            :current-user-id="currentUser?.userId"
            :user-role="workshop?.role"
            :votes-locked="votesLocked"
            :comments-enabled="commentsEnabled"
            :is-detail-open="isDetailPanelOpen && selectedIdeaId === idea.id"
            @click="handleCardClick(idea)"
            @delete="handleDelete"
            @vote="handleVote"
            @edit="handleEdit"
            @tag-click="toggleTag"
            @comment="handleCommentClick"
          />
        </div>

        <!-- Empty state - only show when filters are applied but no matches -->
        <div
          v-if="
            filteredIdeas.length === 0 && !isLoading && rawIdeas?.length > 0 && hasActiveFilters
          "
          class="text-center py-12"
        >
          <p class="text-white/70 text-lg">{{ $t('workshop.noMatchingIdeas') }}</p>
          <button @click="clearFilters" class="mt-2 text-white underline hover:no-underline">
            {{ $t('filters.clearAll') }}
          </button>
        </div>

        <!-- No ideas yet state -->
        <div v-if="rawIdeas?.length === 0 && !isLoading" class="text-center py-12">
          <p class="text-white/70 text-lg">{{ $t('workshop.noIdeasYet') }}</p>
          <p v-if="canWrite" class="text-white/50 mt-2">{{ $t('workshop.clickAddIdea') }}</p>
        </div>
      </div>
    </main>

    <!-- Slide-out menu -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="translate-x-full"
      enter-to-class="translate-x-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="translate-x-0"
      leave-to-class="translate-x-full"
    >
      <div
        v-if="menuOpen"
        class="fixed inset-y-0 right-0 w-80 wood-texture z-80 shadow-2xl overflow-y-auto border-l border-black/20"
      >
        <!-- Wood grain texture overlay -->
        <div
          class="absolute inset-0 opacity-40 pointer-events-none"
          style="
            background-image: url('/wood.jpg');
            background-size: cover;
            background-position: center;
          "
        ></div>

        <div class="p-4 space-y-4 relative z-10">
          <div class="flex items-center justify-between">
            <h2 class="text-xl font-bold text-white drop-shadow-md">{{ $t('common.menu') }}</h2>
            <button
              @click="menuOpen = false"
              class="cursor-pointer p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X class="w-6 h-6" />
            </button>
          </div>

          <!-- Menu actions -->
          <div class="space-y-5 pt-6 border-t border-white/20">
            <button
              v-if="canWrite"
              @click="
                isAddModalOpen = true;
                menuOpen = false;
              "
              class="push-button w-full px-4 py-3 rounded-lg flex items-center justify-center gap-2"
            >
              <Plus class="w-5 h-5" />
              {{ $t('idea.addNew') }}
            </button>

            <!-- Pinned note style buttons - only for owners -->
            <div
              v-if="isOwner"
              class="paper-card bbg-(--color-postit-yellow) p-3 rounded-sm cork-pin"
              style="--card-rotation: -1deg"
            >
              <div class="paper-texture rounded-sm"></div>
              <div class="relative z-10 space-y-2">
                <button
                  @click="handleExport"
                  class="w-full px-4 py-2.5 bg-black/10 text-gray-800 rounded-lg hover:bg-black/20 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Download class="w-5 h-5" />
                  {{ $t('common.export') }}
                </button>
                <button
                  @click="handleImportClick"
                  class="w-full px-4 py-2.5 bg-black/10 text-gray-800 rounded-lg hover:bg-black/20 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Upload class="w-5 h-5" />
                  {{ $t('common.import') }}
                </button>
              </div>
            </div>

            <div
              class="paper-card bg-(--color-postit-blue) p-3 rounded-sm cork-pin"
              style="--card-rotation: 0.5deg"
            >
              <div class="paper-texture rounded-sm"></div>
              <div class="relative z-10 space-y-2">
                <button
                  v-if="isOwner"
                  @click="
                    showInviteManager = true;
                    menuOpen = false;
                  "
                  class="w-full px-4 py-2.5 bg-black/10 text-gray-800 rounded-lg hover:bg-black/20 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Users class="w-5 h-5" />
                  <span>{{ $t('common.access') }}</span>
                </button>
                <button
                  v-if="isOwner"
                  @click="
                    showSupporters = true;
                    menuOpen = false;
                  "
                  class="w-full px-4 py-2.5 bg-black/10 text-gray-800 rounded-lg hover:bg-black/20 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <HandHelping class="w-5 h-5" />
                  <span>{{ $t('common.supporters') }}</span>
                </button>
                <button
                  v-if="isOwner"
                  @click="
                    showComments = true;
                    selectedCommentIdeaId = null;
                    menuOpen = false;
                  "
                  class="w-full px-4 py-2.5 bg-black/10 text-gray-800 rounded-lg hover:bg-black/20 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <MessageSquare class="w-5 h-5" />
                  <span>{{ $t('common.comments') }}</span>
                </button>
                <button
                  @click="
                    showSettings = true;
                    menuOpen = false;
                  "
                  class="w-full px-4 py-2.5 bg-black/10 text-gray-800 rounded-lg hover:bg-black/20 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <Settings class="w-5 h-5" />
                  <span>{{ $t('settings.title') }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Menu overlay -->
    <div v-if="menuOpen" class="fixed inset-0 bg-black/50 z-75" @click="menuOpen = false"></div>

    <!-- Floating add button (shown when hamburger menu is visible - narrow screens) -->
    <button
      v-if="canWrite && !isDesktop && !menuOpen && !isFilterOpen"
      @click="isAddModalOpen = true"
      class="fixed bottom-6 right-6 w-14 h-14 bg-(--color-primary) text-white rounded-full shadow-lg hover:scale-110 transition-transform flex items-center justify-center z-30"
    >
      <Plus class="w-7 h-7" />
    </button>

    <!-- Modals -->
    <AddIdeaModal
      v-model="isAddModalOpen"
      :is-mobile="isMobile"
      :all-tags="allTags"
      :prefill-person="prefillNameEnabled ? userName : ''"
      @add="handleAddIdea"
    />

    <AddIdeaModal
      v-model="isEditModalOpen"
      :is-mobile="isMobile"
      :all-tags="allTags"
      :edit-mode="true"
      :idea="editingIdea || undefined"
      @edit="handleSaveEdit"
    />

    <IdeaDetailPanel
      :idea="selectedIdea"
      :is-open="isDetailPanelOpen"
      :can-write="canWrite"
      :current-user-id="currentUser?.userId"
      :user-role="workshop?.role"
      :votes-locked="votesLocked"
      :comments-enabled="commentsEnabled"
      @close="
        isDetailPanelOpen = false;
        selectedIdeaId = null;
      "
      @vote="
        (type: 'liked' | 'important' | 'supported', onError?: () => void) => {
          if (selectedIdea) {
            handleVote(selectedIdea.id, type, onError);
          }
        }
      "
      @tag-click="toggleTag"
      @delete="selectedIdea && (handleDelete(selectedIdea.id), (isDetailPanelOpen = false))"
      @edit="handleDetailEdit"
      @comment="handleDetailComment"
    />

    <!-- Settings Panel -->
    <SettingsPanel
      v-model="showSettings"
      :workshop="workshop || null"
      @show-welcome="handleReopenWelcomeModal"
    />

    <!-- Access Manager - only for owners -->
    <AccessManager
      v-if="workshop?.id && isOwner"
      v-model="showInviteManager"
      :workshop-id="workshop.id"
    />

    <!-- Supporters Modal - only for owners -->
    <SupportersModal
      v-if="workshop?.id && isOwner"
      v-model="showSupporters"
      :workshop-id="workshop.id"
    />

    <!-- Comments Modal -->
    <CommentsModal
      v-if="workshop?.id && showComments"
      v-model="showComments"
      :mode="selectedCommentIdeaId ? 'idea' : 'workshop'"
      :idea-id="selectedCommentIdeaId as Id<'ideas'> | undefined"
      :idea-title="
        selectedCommentIdeaId
          ? rawIdeas.find(i => i.id === selectedCommentIdeaId)?.title
          : undefined
      "
      :workshop-id="workshop.id"
      :current-user-id="currentUser?.userId"
      :is-owner="isOwner"
      :comments-enabled="commentsEnabled"
    />

    <!-- Hidden file input -->
    <input ref="fileInput" type="file" accept=".json" class="hidden" @change="handleFileImport" />

    <!-- Confirm Dialog for Reset -->
    <ConfirmDialog
      v-model="showResetConfirm"
      :title="$t('confirm.resetFiltersTitle')"
      :message="$t('confirm.resetFiltersMessage')"
      :confirm-text="$t('common.reset')"
      :cancel-text="$t('common.cancel')"
      confirm-color="destructive"
      @confirm="confirmReset"
    />

    <!-- Confirm Dialog for Import -->
    <ConfirmDialog
      v-model="showImportConfirm"
      :title="$t('confirm.importIdeasTitle')"
      :message="$t('confirm.importIdeasMessage')"
      :confirm-text="$t('common.import')"
      :cancel-text="$t('common.cancel')"
      confirm-color="primary"
      @confirm="confirmImport"
    />

    <!-- Toast Notifications -->
    <Toast ref="toastRef" />

    <!-- Welcome Modal -->
    <WelcomeModal
      v-model="showWelcomeModal"
      :comments-enabled="commentsEnabled"
      @hide-permanently="handleHideWelcomeModal"
    />
  </div>
</template>
